import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { audioThunks } from 'store/features/audio/thunks'
import { creditsThunks } from 'store/features/credits/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { ttsThunks } from 'store/features/tts/thunks'
import { userSelectors } from 'store/features/user/selectors'
import { userThunks } from 'store/features/user/thunks'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import {
  AI_VIDEO_REFERENCE_IMAGE_STORAGE_KEY,
  NEW_PROJECT_ID_PARAM,
  SMALL_DESKTOP_SIZE_BREAKPOINT
} from 'consts'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { useResponsive } from 'hooks/useResponsive'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { removeOldLoadingProgressDataFromLocalStorage } from 'utils/localStorage'
import { ReferenceMediaImageType } from 'types/aiVideo'
import { createReferenceMediaItem } from 'store/features/aiVideo/utils'
import { ReferenceMode } from './SubSidebar/AIVideoContent/consts'

export const useUserSounds = () => {
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)

  useEffect(() => {
    if (isUserLoggedIn) {
      dispatch(audioThunks.getUserSounds())
    }
  }, [isUserLoggedIn])
}

export const useFeatureCredits = () => {
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)

  useEffect(() => {
    if (isUserLoggedIn) {
      dispatch(creditsThunks.getUserCredits())
    }
  }, [isUserLoggedIn])
}

export const useTtsDataSync = () => {
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)
  const ttsAudios = useSelector(projectDataSelectors.ttsAudios)
  useEffect(() => {
    if (ttsAudios) {
      dispatch(ttsThunks.syncTtsAudios())
    }
  }, [ttsAudios])

  useEffect(() => {
    if (isUserLoggedIn) {
      dispatch(userThunks.getActiveHighestSubscription())
      dispatch(ttsThunks.getUserTtsCredits())
    }
  }, [isUserLoggedIn])
}

export const useAIImageReference = () => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  useEffect(() => {
    const referenceImage = localStorage.getItem(
      AI_VIDEO_REFERENCE_IMAGE_STORAGE_KEY
    )

    if (referenceImage) {
      if (isMobile) {
        dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
        dispatch(editorThunks.setIsMobileSubSidebarOpen(true))
      }

      dispatch(
        //TODO: remove the ?${Date.now()} after image cross origin is fixed
        aiVideoThunks.addReferenceMediaItem({
          item: createReferenceMediaItem(
            ReferenceMediaImageType.FIRST_FRAME,
            `${referenceImage}?${Date.now()}`
          ),
          bucket: ReferenceMode.FRAMES
        })
      )

      localStorage.removeItem(AI_VIDEO_REFERENCE_IMAGE_STORAGE_KEY)
    }
  }, [isMobile])
}

export const useImageToVideoAiLandingData = () => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const { search, pathname } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(search)
    if (params.get('landing') !== 'fromImageToVideoAi') {
      return
    }

    const removeLandingParam = () => {
      params.delete('landing')
      const newSearch = params.toString()
      navigate(
        { pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      )
    }

    void dispatch(editorThunks.applyImageToVideoAiLandingData(isMobile)).then(
      removeLandingParam
    )
  }, [search, pathname, isMobile, navigate])
}

export const useAiAvatarLandingData = () => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)
  const { search, pathname } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const params = new URLSearchParams(search)
    if (params.get('selectedTab') !== 'aiAvatar') {
      return
    }

    // Don't consume the handoff until the user is authorized. An unauthorized
    // visitor triggers the host login flow, which reloads the page; if we
    // applied (and so deleted the IndexedDB record + stripped the `landing`
    // param) beforehand, the data would be gone after the reload. Leaving both
    // intact lets this effect re-run and apply once `isUserLoggedIn` flips true.
    if (!isUserLoggedIn) {
      return
    }

    const removeLandingParam = () => {
      params.delete('landing')
      const newSearch = params.toString()
      navigate(
        { pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      )
    }

    void dispatch(editorThunks.applyAiAvatarLandingData(isMobile)).then(
      removeLandingParam
    )
  }, [search, pathname, isMobile, navigate, isUserLoggedIn])
}

export const useOpenMobileAIVideoSidebar = () => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const activeTab = params.get('activeTab')
  const { projectId } = useParams()

  useEffect(() => {
    const isNewProject = !projectId || projectId === NEW_PROJECT_ID_PARAM
    if (isMobile && (activeTab === SUB_SIDEBAR_KEYS.AI_VIDEO || isNewProject)) {
      dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
      dispatch(editorThunks.setIsMobileSubSidebarOpen(true))
    }
  }, [isMobile, activeTab, projectId])
}

export const useRemoveOldLoadingProgressData = () => {
  useEffect(() => {
    removeOldLoadingProgressDataFromLocalStorage()
  }, [])
}

export const useFetchLibraryFonts = () => {
  useEffect(() => {
    dispatch(editorThunks.getLibraryFonts())
  }, [])
}
